import { Injectable } from '@angular/core';
import { Observable } from 'rxjs/Observable';
import * as io from 'socket.io-client';
import { Server, SocketIO } from 'mock-socket';

import {
  PageEventsHandler,
  Utils,
  Translate
} from './';
import {
  SOCKET_BASE_URL,
  REQUEST_FULLSCREEN,
  DEBUG,
  USE_MOCK,
  MOCK, CS_F_VERSION,
  TIME_BEFORE_REDIRECT_DUPLICATE_LOGIN,
  WELCOME_PAGE
} from '../constants';

@Injectable()
export class SocketHandler {
  useMock: boolean = USE_MOCK;
  mock: Object = MOCK;

  message$: Observable<any>;
  connect$: Observable<any>;
  disconnect$: Observable<any>;
  isConnected: boolean = false;
  fullscreenExit$: Observable<any>;

  manuallyDisconnected: boolean = false;

  socket;
  mockServer;

  translations: Object = {};

  requestFullscreen: boolean;

  constructor(
    private pageEventsHandler: PageEventsHandler,
    private utils: Utils,
    private translate: Translate
  ) {
    this.requestFullscreen = REQUEST_FULLSCREEN && this.utils.isWeb();

    if (this.useMock) {
      this.mockServer = window['mockServer'] || new Server(SOCKET_BASE_URL);
      this.socket = window['mockSocket'] || SocketIO(SOCKET_BASE_URL);
      window['mockServer'] = this.mockServer;
      window['mockSocket'] = this.socket;
    } else {
      let baseUrl = SOCKET_BASE_URL;
      // global socket url control
      if(typeof window['overrideableVar'] === 'object' && window['overrideableVar'].socketBaseUrl){
        baseUrl = window['overrideableVar'].socketBaseUrl;
      }
      const socketOptions = { query: "code=" + this.utils.getLocalID() + "&csimk=" + this.utils.getCSIMK() };
      this.socket = io(baseUrl, socketOptions);
    }

    this.message$ = Observable
      .create($ => this.socket.on('message', m => $.next(m)));
    this.connect$ = Observable
      .create($ => this.socket.on('connect', () => {
        //console.log("SocketHandler - this.connect$ - Socket connected");
        this.isConnected = true;
        $.next();
      }));
    this.disconnect$ = Observable
      .create($ => this.socket.on('disconnect', () => {
        //console.log("SocketHandler - this.disconnect$ - Socket disconnected");
        this.isConnected = false;
        if (this.manuallyDisconnected) {
          return;
        }
        /*
        if (this.socket) {
          this.socket.disconnect(true);
          this.socket = null;
          Observable.timer(5000).subscribe(() => {this.socket = io(SOCKET_BASE_URL)});
        }
        */
        $.next();
      }));

    if (DEBUG) {
      this.connect$.subscribe(() => console.log('conne ', this.socket.id));
      this.disconnect$.subscribe(() => console.log('disconnect '));
      this.message$.subscribe(e => console.log('message1 ', e));
    }

    this.fullscreenExit$ = this.pageEventsHandler.fullscreen$
      .filter(isFullscreen => !isFullscreen)
      .filter(() => this.requestFullscreen);

    this.translate.getTranslations$()
      .subscribe(translations => this.translations = translations);

    // on force disconnect redirect
    this.message$
      .filter(m => m.type === 'force.disconnect.duplicate')
      .subscribe(m => {
        this.manuallyDisconnect();
        Observable.timer(TIME_BEFORE_REDIRECT_DUPLICATE_LOGIN).subscribe(() => {
          // fix tablet
          let welcomePage = WELCOME_PAGE;
          if(this.utils.isMobile() || this.utils.isDesktop()){
            welcomePage = 'welcome_local.html';
          }
          window.location.href = welcomePage;
        });
      });
  }

  manuallyDisconnect() {
    if (this.socket && typeof this.socket.disconnect !== 'undefined') {
      this.manuallyDisconnected = true;
      this.socket.disconnect(true);
    }
  }

  _showAlert(message) {
    let toPrint = message;

    if (typeof message === 'object' &&
      message.payload && message.payload.message) {
      toPrint = decodeURI(message.payload.message);
    }

    console.error(toPrint);
  }

  emit(message) {
    if (DEBUG) {
      console.log('emit ', message);
    }

    if (typeof message !== 'object' || message === null) {
      console.error('Emit format error');
      return;
    }

    let currentType = message.type.split('.')[0];
    setTimeout(() => {
      message.cv = CS_F_VERSION;
      message.localID = this.utils.getLocalID();
      message.localIP = this.utils.getLocalIP();
      message.timestamp = +(new Date());
      this.socket.emit('message', message);

      if (this.useMock &&
        typeof this.mock[currentType] !== 'undefined') {
        this.mockServer.send({
          messageId: message.messageId,
          type: currentType + '.response.success',
          payload: this.mock[currentType]['response.success']
        });
      }
    }, 10);
  }

  guiEventRequest(key: string, data?) {
    let _payload = {
      key: key,
      data: data || {}
    };

    this.request$('gui_event', _payload);
  }

  request$(...args: any[]): Observable<any> {
    let [_type, _payload, useTimeout, addCheck, addTerminate$, nOfRetry, timeoutSecs] = args;

    let timeout$ = this.utils.requestTimeout$(_type + ' timeout', timeoutSecs)
      , uid = this.utils.generateUID()
      , attempCount = 0;

    let terminalEvents$, terminalEvents = [
      // this.fullscreenExit$.mapTo('Fullscreen exit'), // TODO: keep or remove?
      this.disconnect$.mapTo(this.translations['global#no_connection'])
    ];

    let _isValidResponse = (requestType, responseType) =>
      responseType === requestType + '.response.success' ||
      responseType === requestType + '.response.fail';

    let _execute = () => this.emit({
      messageId: uid,
      type: _type + '.request',
      payload: _payload
    });

    if (addTerminate$) {
      terminalEvents.push(addTerminate$);
    }

    if (useTimeout) {
      terminalEvents.push(timeout$);
    }

    terminalEvents$ = Observable.merge(...terminalEvents);

    let response$ = this.message$
      .takeUntil(terminalEvents$)
      .filter(message =>
        message && message.payload)
      .filter(({messageId}) => messageId === uid)
      .filter(({type}) => {
        if (nOfRetry &&
          attempCount < nOfRetry &&
          type === _type + '.response.fail') {
          console.error(_type + '.response.fail - attemp retry');
          attempCount++;

          Observable.timer(1000)
            .subscribe(() => _execute());

          return false;
        }

        return _isValidResponse(_type, type);
      })
      .map(({type, payload}) => {
        if (type === _type + '.response.fail') {
          throw payload.message;
        }

        if (addCheck && addCheck.callback) {
          if (!addCheck.callback
            .call(null, payload, ...addCheck.args)) {
            throw 'Qualcosa è andato storto';
          }
        }

        return payload;
      });

    let return$ = Observable.merge(
      response$,
      terminalEvents$
        .do(err => this._showAlert(err))
        .switchMap(err => Observable.throw(err)));

    _execute();

    return return$.take(1);
  }
}



// WEBPACK FOOTER //
// ./src/app/services/socket-handler.service.ts